import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ShoppingBag from 'lucide-react/dist/esm/icons/shopping-bag.js';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2.js';
import Edit from 'lucide-react/dist/esm/icons/edit-2.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import { products, printAreas, colorLabelsHe } from '@/data/products';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import CheckoutModal from '@/components/CheckoutModal';
import DeliveryOptions from '@/components/DeliveryOptions';
import { DEFAULT_DISCOUNT_RATE } from '@/lib/discountHelpers';
import { cartItemQuantity, evaluateCoupon, priceCart } from '@/lib/couponPricing';
import { resolveOrderIdempotencyKey } from '@/lib/orderSessionIdentity';

const Cart = () => {
  const { t, language } = useLanguage();
  const { cartItems, removeFromCart, payload, mergePayload, getTotalItems } = useCart();
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [couponInput, setCouponInput] = useState(() => payload?.couponCode || '');
  const [couponMessage, setCouponMessage] = useState('');
  const [areaDialogOpen, setAreaDialogOpen] = useState(false);
  const invalidAreaMessage = language === 'he'
    ? 'הקופון לא הוחל. אפשר לעדכן את אזורי ההדפסה או להמשיך לתשלום רגיל.'
    : 'Coupon not applied. Change the print areas or continue at regular prices.';
  const mixedAreaMessage = language === 'he'
    ? 'הקופון הוחל רק על פריטים מתאימים. פריטים עם אזורי הדפסה אחרים מתומחרים במחיר רגיל.'
    : 'The coupon applies only to qualifying items. Items with other print areas keep their regular price.';
  const paymentFailureToastShownRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const paymentStatus = new URLSearchParams(location.search).get('status');
    if (paymentStatus !== 'failed' || paymentFailureToastShownRef.current) return;

    const timeoutId = window.setTimeout(() => {
      if (paymentFailureToastShownRef.current) return;

      paymentFailureToastShownRef.current = true;
      toast({
        variant: 'destructive',
        title: language === 'he' ? 'התשלום בכרטיס לא אושר' : 'Card payment was not approved',
        description: language === 'he'
          ? 'החיוב נדחה על ידי חברת האשראי או שהתשלום לא הושלם. ההזמנה לא סומנה כמשולמת ולא הועברה לייצור. אפשר לנסות שוב או לבחור אמצעי תשלום אחר.'
          : 'The card charge was declined or not completed. The order was not marked as paid or sent to production. You can try again or choose another payment method.',
        duration: 12000,
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [language, location.search, toast]);

  const discountClaimed = (() => {
    if (payload?.discountClaimed) return true;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return window.sessionStorage.getItem('printee:discount-claimed') === 'true';
      }
    } catch (err) {
      // ignore storage access failures
    }
    return false;
  })();

  const currencyFormatter = useMemo(() => {
    const locale = language === 'he' ? 'he-IL' : 'en-IL';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch {
      return new Intl.NumberFormat('en-IL', {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }, [language]);

  const formatCurrency = useCallback((value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    try {
      return currencyFormatter.format(safe);
    } catch {
      return `₪${safe.toFixed(2)}`;
    }
  }, [currencyFormatter]);

  const totalItems = typeof getTotalItems === 'function' ? getTotalItems() : 0;
  const pricing = useMemo(() => priceCart({
    cartItems,
    couponCode: payload?.couponCode,
    discountClaimed,
    withDelivery: payload?.withDelivery,
    totalItemsCount: totalItems,
  }), [cartItems, payload?.couponCode, payload?.withDelivery, discountClaimed, totalItems]);
  const derivedItems = pricing.lines;
  const {
    subtotalBeforeDiscount: totalBeforeDiscount,
    discountAmount: discountTotal,
    subtotalAfterDiscount,
    deliveryCost,
    vatAmount,
    couponDiscount,
    paymentTotal: grandTotal,
  } = pricing;

  const firstDiscountedItem = derivedItems.find((entry) => entry.discountAmount > 0);
  const discountRatePercent = firstDiscountedItem
    ? Math.round(((firstDiscountedItem.discountRate || DEFAULT_DISCOUNT_RATE) || 0) * 100)
    : Math.round(DEFAULT_DISCOUNT_RATE * 100);
  const discountLabelValue = t('discountLabel');
  const standardDiscountLabelText = typeof discountLabelValue === 'function'
    ? discountLabelValue(discountRatePercent)
    : discountLabelValue;
  const discountLabelText = pricing.couponApplied && pricing.coupon.type === 'fixed_unit_gross'
    ? `${language === 'he' ? 'קופון' : 'Coupon'} ${pricing.coupon.code}`
    : standardDiscountLabelText;

  const handleApplyCoupon = (event) => {
    event.preventDefault();
    const candidate = evaluateCoupon(couponInput, cartItems);
    if (candidate.status === 'applied') {
      mergePayload({ couponCode: candidate.code });
      setCouponMessage(candidate.invalidAreas ? mixedAreaMessage : '');
      if (candidate.invalidAreas) setAreaDialogOpen(true);
      return;
    }
    if (candidate.status === 'invalid_areas') {
      if (payload?.couponCode === candidate.code) mergePayload({ couponCode: '' });
      setCouponMessage(invalidAreaMessage);
      setAreaDialogOpen(true);
      return;
    }
    setCouponMessage(candidate.status === 'no_eligible_items'
      ? (candidate.type === 'order_percent'
        ? (language === 'he' ? 'יש להוסיף פריט לעגלה כדי להשתמש בקוד.' : 'Add an item to the cart to use this code.')
        : (language === 'he' ? 'הקוד זמין לחולצת טריקו, סווטשירט וקפוצ׳ון בלבד.' : 'This code is available for T-shirts, sweatshirts, and hoodies only.'))
      : (language === 'he' ? 'קוד הקופון אינו מוכר.' : 'Coupon code not recognized.'));
  };

  useEffect(() => {
    if (!payload?.couponCode) return;
    const current = evaluateCoupon(payload.couponCode, cartItems);
    if (!current.invalidAreas) return;
    if (current.status === 'invalid_areas') {
      mergePayload({ couponCode: '' });
      setCouponMessage(invalidAreaMessage);
    } else {
      setCouponMessage(mixedAreaMessage);
    }
    setAreaDialogOpen(true);
  }, [payload?.couponCode, cartItems, mergePayload, invalidAreaMessage, mixedAreaMessage]);

  const handleRemoveCoupon = () => {
    mergePayload({ couponCode: '' });
    setCouponInput('');
    setCouponMessage('');
  };

  const handleCheckout = () => {
    if (pricing.ok === false) return;
    // Airtable record/order ids and asset links can exist before payment. They
    // belong to the current order session and must not rotate its retry key.
    const { idempotencyKey, created } = resolveOrderIdempotencyKey(payload?.idempotency_key);
    if (created || payload?.idempotency_key !== idempotencyKey) {
      try { mergePayload({ idempotency_key: idempotencyKey }); } catch {}
    }

    // Note: Airtable webhook is now sent when user accepts discount in DiscountPopup
    // This ensures we capture customer details early when they claim the 5% discount

    setModalOpen(true);
  };

  // Always scroll to top on mount so user starts at the top of the cart page
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch { window.scrollTo(0, 0); }
  }, []);

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
    toast({
      title: 'הפריט הוסר מהעגלה'
    });
  };

  if (cartItems.length === 0) {
    return (
      <>
        <div className="min-h-screen py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
              >
                <ShoppingBag className="h-24 w-24 text-gray-300 mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  {t('cartEmpty')}
                </h1>
                <p className="text-gray-600 mb-8">
                  התחל בתהליך הזמנה עכשיו!
                </p>
                <Button asChild size="lg">
                  <Link to="/catalog">
                    {t('continueShopping')}
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Dialog open={areaDialogOpen} onOpenChange={setAreaDialogOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>יש לעדכן את אזורי ההדפסה</DialogTitle>
            <DialogDescription>
              תמחור הזמנה של טיול שנתי כפר הירוק כולל הדפס סמל קדימה ואיי4 אחורה. בבקשה לשנות את האיזורי הדפסה.
            </DialogDescription>
          </DialogHeader>
          <Button type="button" onClick={() => setAreaDialogOpen(false)}>OK</Button>
        </DialogContent>
      </Dialog>
      <div className="min-h-screen py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('cartTitle')}
            </h1>
            <p className="text-gray-600">
              סקור את הפריטים בעגלתך והמשך לתשלום
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <img
                        src={(() => {
                          try {
                            const prod = products.find(p => p.sku === item.productSku);
                            const color = item.color || (prod && prod.colors && prod.colors[0]);
                            const list = prod && prod.images && prod.images[color];
                            if (Array.isArray(list) && list.length) {
                              const prefer = list.find(p => p.endsWith('.jpg') || p.endsWith('.jpeg') || p.endsWith('.png'));
                              return prefer || list[0];
                            }
                            const baseList = prod && prod.images && prod.images.base1;
                            if (Array.isArray(baseList) && baseList.length) {
                              const preferBase = baseList.find(p => p.endsWith('.jpg') || p.endsWith('.jpeg') || p.endsWith('.png'));
                              return preferBase || baseList[0];
                            }
                          } catch (e) {}
                          return item.mockupUrl;
                        })()}
                        alt={item.productName}
                        className="w-24 h-24 object-cover rounded-lg"
                        onError={(e) => {
                          const el = e.currentTarget;
                          try {
                            const prod = products.find(p => p.sku === item.productSku);
                            const color = item.color || (prod && prod.colors && prod.colors[0]);
                            const list = prod && prod.images && prod.images[color];
                            if (Array.isArray(list) && list.length) {
                              // find current index by matching end of path using pathname
                              try {
                                const pathname = new URL(el.src, window.location.origin).pathname;
                                let idx = list.findIndex(path => pathname.endsWith(path));
                                if (idx === -1) idx = 0;
                                if (idx >= 0 && idx < list.length - 1) {
                                  el.src = list[idx + 1];
                                  return;
                                }
                              } catch (err) {
                                // fallback to string matching
                                let idx = list.findIndex(path => el.src.endsWith(path));
                                if (idx === -1) idx = 0;
                                if (idx >= 0 && idx < list.length - 1) {
                                  el.src = list[idx + 1];
                                  return;
                                }
                              }
                            }
                          } catch (err) {
                            // ignore
                          }
                          if (el.src.endsWith('.webp')) el.src = el.src.replace('.webp', '.jpg');
                          else if (el.src.endsWith('.jpg')) el.src = el.src.replace('.jpg', '.jpeg');
                          else if (el.src.endsWith('.jpeg')) el.src = el.src.replace('.jpeg', '.png');
                        }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {item.productName}
                      </h3>
                      {/* Compact color x size table: rows = colors, cols = sizes */}
                      {(() => {
                        const matrices = item.sizeMatrices || {};
                        const activeColors = (item.selectedColors || []).filter(c => {
                          const mat = matrices[c] || {};
                          return Object.values(mat).reduce((s, q) => s + (q || 0), 0) > 0;
                        });

                        if (activeColors.length > 0) {
                          // Prefer the product's canonical sizeRange so columns are uniform
                          const prod = products.find(p => p.sku === item.productSku) || {};
                          let sizes = Array.isArray(prod.sizeRange) && prod.sizeRange.length
                            ? prod.sizeRange.slice()
                            : null;

                          // If product doesn't declare sizes, fall back to collecting sizes from matrices
                          if (!sizes) {
                            const sizesSet = new Set();
                            activeColors.forEach(c => {
                              const mat = matrices[c] || {};
                              Object.keys(mat || {}).forEach(sz => sizesSet.add(sz));
                            });
                            sizes = Array.from(sizesSet);
                          }

                          return (
                            <div className="overflow-auto mb-2">
                              <table className="w-full text-sm text-gray-700 border-collapse">
                                <thead>
                                  <tr>
                                    <th className="text-left pr-4 pb-2 font-medium">{language === 'he' ? 'צבע' : 'Color'}</th>
                                    {sizes.map((sz, i) => (
                                      <th
                                        key={sz}
                                        className={`text-center px-3 pb-2 font-medium border-l border-gray-200`}
                                      >
                                        {t(sz) || sz.toUpperCase()}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeColors.map(colorKey => {
                                    const mat = matrices[colorKey] || {};
                                    return (
                                      <tr key={colorKey} className="border-t">
                                        <td className="py-2 pr-4">{language === 'he' ? (colorLabelsHe[colorKey] || colorKey) : colorKey}</td>
                                        {sizes.map((sz) => {
                                          const qty = mat[sz] || 0;
                                          return (
                                            <td key={sz} className={`text-center px-3 py-2 border-l border-gray-200`}>
                                              {qty > 0 ? qty : ''}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        }

                        // Legacy single-color fallback (list sizes)
                        return (
                          <div className="text-sm text-gray-500 mb-2">
                            {Object.entries(item.sizeMatrix || {})
                              .filter(([size, qty]) => qty > 0)
                              .map(([size, qty]) => `${t(size) || size.toUpperCase()}: ${qty}`)
                              .join(', ')}
                          </div>
                        );
                      })()}

                      {/* Print areas */}
                      <div className="text-sm text-gray-500 mb-3">
                        אזורי הדפסה: {item.selectedPrintAreas.map(s => {
                          const sel = typeof s === 'string' ? { areaKey: s, method: 'print' } : s;
                          const area = printAreas[sel.areaKey];
                          const methodLabel = sel.method === 'embo' ? (language === 'he' ? 'רקמה' : 'Embo') : (language === 'he' ? 'הדפסה' : 'Print');
                          return `${area?.labelHe || area?.label || sel.areaKey} (${methodLabel})`;
                        }).join(', ')}
                      </div>

                      {(() => {
                        const toNumber = (val) => {
                          const num = Number(val);
                          return Number.isFinite(num) ? num : 0;
                        };
                        const derived = derivedItems[index];
                        const itemDiscountAmount = derived?.discountAmount ?? toNumber(item.discountAmount);
                        const itemTotal = derived?.subtotalAfter ?? toNumber(item.totalPrice);
                        const couponItemTotalInclVat = derived?.couponApplied
                          ? derived.couponUnitPriceInclVat * cartItemQuantity(item)
                          : null;
                        const itemDiscountRate = derived?.discountRate ?? toNumber(item.discountRate);
                        const itemDiscountLabel = derived?.couponApplied
                          ? `${language === 'he' ? 'קופון' : 'Coupon'} ${pricing.coupon.code}`
                          : typeof discountLabelValue === 'function'
                          ? discountLabelValue(Math.round(((itemDiscountRate || DEFAULT_DISCOUNT_RATE) || 0) * 100))
                          : discountLabelValue;

                        return (
                          <div className={`flex justify-between gap-4 ${language === 'he' ? 'flex-row-reverse' : ''}`}>
                            <div className={`space-y-1 ${language === 'he' ? 'text-right' : 'text-left'}`}>
                              {pricing.ok !== false && itemDiscountAmount > 0 && (
                                <div className="text-sm text-green-600">
                                  {itemDiscountLabel}: -{formatCurrency(itemDiscountAmount)}
                                </div>
                              )}
                              {pricing.ok !== false && derived?.couponApplied && (
                                <div className="text-sm text-green-700">
                                  {language === 'he' ? 'מחיר קופון ליחידה כולל מע״מ' : 'Coupon unit price incl. VAT'}: {formatCurrency(derived.couponUnitPriceInclVat)}
                                </div>
                              )}
                              <div className="text-lg font-semibold text-blue-600">
                                {language === 'he' ? 'סה״כ לפריט כולל מע״מ' : 'Item total incl. VAT'}: {pricing.ok === false
                                  ? (language === 'he' ? 'מחיר לא זמין' : 'Price unavailable')
                                  : formatCurrency(couponItemTotalInclVat ?? itemTotal)}
                              </div>
                            </div>

                            <div className={`flex gap-2 ${language === 'he' ? 'flex-row-reverse' : ''}`}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/product/${item.productSku}`, { state: { prefill: item } })}
                                className="active:translate-y-px motion-reduce:transform-none"
                              >
                                <Edit className={`h-4 w-4 ${language === 'he' ? 'ml-1' : 'mr-1'}`} />
                                {t('editItem')}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveItem(item.id)}
                                className="text-red-600 hover:text-red-700 active:translate-y-px motion-reduce:transform-none"
                              >
                                <Trash2 className={`h-4 w-4 ${language === 'he' ? 'ml-1' : 'mr-1'}`} />
                                {t('removeItem')}
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
                ))}

              {/* Delivery options: moved into cart body above upsell */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                <DeliveryOptions
                  withDelivery={payload?.withDelivery || false}
                  freeDelivery={pricing.couponApplied && pricing.coupon.freeDelivery}
                  onDeliveryChange={(v)=>{ mergePayload({ withDelivery: !!v }); }}
                  contact={payload?.contact || {}}
                  onContactChange={(c) => { mergePayload({ contact: c }); }}
                />
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl p-6 shadow-lg lg:sticky lg:top-8"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                  סיכום הזמנה
                </h2>

                <div className="space-y-4 mb-6">
                  <form onSubmit={handleApplyCoupon} className="space-y-2">
                    <label htmlFor="cart-coupon-code" className="block text-sm font-medium text-gray-700">
                      {language === 'he' ? 'קוד קופון' : 'Coupon code'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="cart-coupon-code"
                        type="text"
                        dir="ltr"
                        value={couponInput}
                        onChange={(event) => { setCouponInput(event.target.value); setCouponMessage(''); }}
                        autoComplete="off"
                        className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      />
                      <Button type="submit" variant="outline">{language === 'he' ? 'החל' : 'Apply'}</Button>
                    </div>
                    {pricing.couponApplied && (
                      <div className="flex items-center justify-between gap-2 text-sm text-green-700">
                        <span>{language === 'he' ? `הקופון ${pricing.coupon.code} הוחל` : `Coupon ${pricing.coupon.code} applied`}</span>
                        <button type="button" onClick={handleRemoveCoupon} className="underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                          {language === 'he' ? 'הסר' : 'Remove'}
                        </button>
                      </div>
                    )}
                    <p role="status" aria-live="polite" className="text-sm text-red-700">
                      {couponMessage || (payload?.couponCode && !pricing.couponApplied
                        ? (language === 'he' ? 'הקופון אינו מתאים עוד לפריטים בעגלה.' : 'This coupon no longer applies to the cart.')
                        : '')}
                    </p>
                  </form>
                  {pricing.ok === false ? (
                    <p role="alert" className="text-sm font-medium text-red-700">
                      {language === 'he' ? 'לא ניתן לחשב מחיר לאחד הפריטים. יש לערוך את הפריט לפני המשך לתשלום.' : 'A cart item could not be priced. Edit the item before checkout.'}
                    </p>
                  ) : <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('subtotalBeforeDiscount')}</span>
                      <span className="font-medium">{formatCurrency(totalBeforeDiscount)}</span>
                    </div>
                  {discountTotal > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{discountLabelText}</span>
                      <span>-{formatCurrency(discountTotal)}</span>
                    </div>
                  )}
                  {payload?.withDelivery && (deliveryCost > 0 || (pricing.couponApplied && pricing.coupon.freeDelivery)) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{language === 'he' ? 'עלות משלוח' : 'Delivery cost'}</span>
                      <span className="font-medium">{deliveryCost > 0 ? '+' : ''}{formatCurrency(deliveryCost)}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{language === 'he' ? 'קופון test99 (99%)' : 'Coupon test99 (99%)'}</span>
                      <span>-{formatCurrency(couponDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vatLabel')}</span>
                    <span className="font-medium">{formatCurrency(vatAmount)}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold">{language === 'he' ? 'סה״כ כולל מע״מ' : 'Total incl. VAT'}</span>
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(grandTotal)}
                      </span>
                    </div>
                  </div>
                  </>}
                </div>

                <Button 
                  className="w-full mb-4 active:scale-[0.99] motion-reduce:transform-none"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={pricing.ok === false}
                  aria-haspopup="dialog"
                  aria-expanded={modalOpen}
                  aria-controls="checkout-payment-dialog"
                >
                  {language === 'he' ? 'המשך לתשלום' : t('checkout')}
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>

                {(() => {
                  const prefill = {
                    ...(payload?.contact || {}),
                    // If delivery contact exists, prefer its name/phone as prefill fallbacks
                    name: (payload?.contact?.name || payload?.contact?.fullName || ''),
                    phone: (payload?.contact?.phone || ''),
                    email: (payload?.contact?.email || ''),
                  };
                  return (
                    <CheckoutModal
                      open={modalOpen}
                      onClose={()=>setModalOpen(false)}
                      cartSummary={{
                        subtotal: totalBeforeDiscount,
                        discount: discountTotal,
                        total: subtotalAfterDiscount
                      }}
                      prefillContact={prefill}
                    />
                  );
                })()}

                <Button asChild variant="outline" className="w-full active:translate-y-px motion-reduce:transform-none">
                  <Link to="/catalog">
                    {language === 'he' ? 'המשך קניות' : t('continueShopping')}
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Upsell area: show other products - moved below the main grid so on mobile it appears at the bottom */}
          <div className="bg-white rounded-xl p-6 shadow-lg mt-6">
            <h3 className="text-lg font-semibold mb-4">עוד מוצרים שאולי יעניינו אותך</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {products.filter(p => !cartItems.some(ci => ci.productSku === p.sku)).slice(0,4).map(p => (
                <Link
                  key={p.sku}
                  to={`/product/${p.sku}`}
                  aria-label={`${language === 'he' ? 'לצפייה במוצר' : 'View product'} ${language === 'he' ? p.nameHe : p.name}`}
                  className="group flex min-h-36 flex-col items-center gap-2 rounded border p-3 transition-colors hover:border-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:bg-blue-50"
                >
                  <img
                    src={(Array.isArray(p.images?.base1) ? (p.images.base1.find(x=>x.endsWith('.jpg')||x.endsWith('.jpeg')||x.endsWith('.png'))||p.images.base1[0]) : `/product_images/${p.sku}/base1_${p.sku}.png`)}
                    alt={p.nameHe}
                    className="h-20 w-20 object-contain transition-transform group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none"
                    onError={(e) => {
                      const el = e.currentTarget;
                      try {
                        const list = Array.isArray(p.images?.base1) ? p.images.base1 : null;
                        if (list && list.length) {
                          try {
                            const pathname = new URL(el.src, window.location.origin).pathname;
                            let idx = list.findIndex(path => pathname.endsWith(path));
                            if (idx === -1) idx = 0;
                            if (idx >= 0 && idx < list.length - 1) {
                              el.src = list[idx + 1];
                              return;
                            }
                          } catch (err) {
                            let idx = list.findIndex(path => el.src.endsWith(path));
                            if (idx === -1) idx = 0;
                            if (idx >= 0 && idx < list.length - 1) {
                              el.src = list[idx + 1];
                              return;
                            }
                          }
                        }
                      } catch (err) {}
                      if (el.src.endsWith('.webp')) el.src = el.src.replace('.webp', '.jpg');
                      else if (el.src.endsWith('.jpg')) el.src = el.src.replace('.jpg', '.jpeg');
                      else if (el.src.endsWith('.jpeg')) el.src = el.src.replace('.jpeg', '.png');
                    }}
                  />
                  <div className="text-sm font-medium">{language === 'he' ? p.nameHe : p.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
